import { SwitcherEntityKind, type SwitcherItem, type WorkspaceSwitcherModel } from '@vizit/access';
import { Button, ButtonLevel, EntityBadge, EntityBadgeSize, Menu, MenuItem, MenuPopover } from '@vizit/atoms';
import { MenuSection, MenuTrigger, Text, TextColor, TextSize, sectionedMenuStyles } from '@vizit/atoms';
import { CheckIcon } from '@vizit/icons';
import { colors, cva } from '@vizit/theme';
import { Header, type Key } from 'react-aria-components';

const styles = {
  ...sectionedMenuStyles,
  item: cva({
    base: [
      'flex items-center gap-3 mx-3 px-5 py-4 rounded cursor-pointer select-none outline-none transition-colors',
      'data-[focused]:bg-primary-hover',
      'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none',
    ],
    variants: { active: { true: 'bg-primary-hover' } },
  }),
  label: cva({ base: 'flex-1 min-w-0' }),
};

export interface WorkspaceSwitcherProps {
  model: WorkspaceSwitcherModel;
  onSelect: (item: SwitcherItem) => void;
  className?: string;
  'aria-label'?: string;
}

function itemKey(sectionKey: string, item: SwitcherItem): string {
  return `${sectionKey}:${item.id}`;
}

function WorkspaceSwitcher(props: WorkspaceSwitcherProps) {
  const { model, onSelect, className, 'aria-label': ariaLabel = 'Switch workspace' } = props;
  const { trigger, sections } = model;

  if (!trigger || sections.length === 0) {
    return null;
  }

  const lookup = new Map<string, SwitcherItem>();
  for (const section of sections) {
    for (const item of section.items) {
      lookup.set(itemKey(section.key, item), item);
    }
  }

  const handleAction = (key: Key) => {
    const item = lookup.get(String(key));
    if (item) {
      onSelect(item);
    }
  };

  return (
    <MenuTrigger>
      <Button
        aria-label={ariaLabel}
        level={ButtonLevel.Ghost}
        style={{ padding: 0 }}
        className={className}
        data-testid="workspace-switcher-button"
        iconStart={<EntityBadge id={trigger.id} name={trigger.name} size={EntityBadgeSize.Lg} />}
      />
      <MenuPopover placement="right bottom" className={styles.popover()} data-testid="workspace-switcher-menu">
        <Menu aria-label={ariaLabel} onAction={handleAction} className={styles.menu()}>
          {sections.map((section, index) => (
            <MenuSection key={section.key} className={styles.section({ withDivider: index > 0 })}>
              {section.label ? (
                <Header className={styles.header({ emphasized: section.kind === SwitcherEntityKind.Workspace })}>
                  {section.label}
                </Header>
              ) : null}
              {section.items.map((item) => {
                const key = itemKey(section.key, item);
                return (
                  <MenuItem
                    key={key}
                    id={key}
                    textValue={item.name}
                    isDisabled={item.targetHref === null}
                    className={styles.item({ active: item.isActive })}
                    data-testid={`workspace-switcher-item-${item.id}`}
                  >
                    <EntityBadge id={item.id} name={item.name} size={EntityBadgeSize.Sm} />
                    <Text
                      size={TextSize.Md}
                      color={TextColor.Inherit}
                      showEllipsis
                      className={styles.label()}
                      title={item.name}
                    >
                      {item.name}
                    </Text>
                    {item.isSelected ? <CheckIcon size={16} color={colors['gray-utility']['700']} /> : null}
                  </MenuItem>
                );
              })}
            </MenuSection>
          ))}
        </Menu>
      </MenuPopover>
    </MenuTrigger>
  );
}

export { WorkspaceSwitcher };
